import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert
} from '@mui/material';
import { Brand, BrandFormData } from '../../types';
import equipmentService from '../../services/equipmentService';

interface BrandFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  brand?: Brand | null;
}

const BrandForm: React.FC<BrandFormProps> = ({
  open,
  onClose,
  onSuccess,
  brand
}) => {
  const [formData, setFormData] = useState<BrandFormData>({
    titulo: brand?.titulo || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof BrandFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro quando o usuário começar a digitar
    if (error) {
      setError(null);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.titulo.trim()) {
      setError('O título da marca é obrigatório');
      return false;
    }

    if (formData.titulo.trim().length < 2) {
      setError('O título deve ter pelo menos 2 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        titulo: formData.titulo.trim()
      };

      if (brand) {
        await equipmentService.updateBrand(brand.id, dataToSend);
      } else {
        await equipmentService.createBrand(dataToSend);
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar marca:', error);
      setError(error?.response?.data?.message || 'Erro ao salvar marca');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      titulo: ''
    });
    setError(null);
    onClose();
  };

  // Resetar form quando abrir para edição
  React.useEffect(() => {
    if (open && brand) {
      setFormData({
        titulo: brand.titulo
      });
    } else if (open && !brand) {
      setFormData({
        titulo: ''
      });
    }
  }, [open, brand]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {brand ? 'Editar Marca' : 'Cadastrar Marca'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Título da Marca"
              value={formData.titulo}
              onChange={(e) => handleInputChange('titulo', e.target.value)}
              required
              placeholder="Ex: TP-Link, Huawei, ZTE..."
              helperText="Nome da marca do equipamento"
              disabled={loading}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Salvando...' : brand ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BrandForm; 